import { z } from 'zod';
import {
  followUpQuestionTypes,
  FOLLOW_UP_FLOOR_DELAY_DAYS,
  FOLLOW_UP_MAX_DELAY_DAYS,
  FOLLOW_UP_MIN_DELAY_DAYS,
  REVIEW_MAX_RATING,
  REVIEW_MIN_RATING,
  type FollowUpStatus,
  type Language,
} from '../enums.js';

/**
 * Contrat réseau du suivi post-consultation et de l'avis patient — `docs/SCREENS.md` §§ « Section 5
 * — Suivi patient » (questions et réponses, jointes par identifiant de question), « Réponse au
 * questionnaire de suivi » et « Avis patient ».
 *
 * Deux surfaces bien distinctes, et c'est volontaire :
 * — côté **cabinet**, derrière une session, on programme un suivi et on lit les réponses ;
 * — côté **patient**, sans session, un jeton à usage unique ouvre une page qui ne montre que ce
 *   qu'il faut pour répondre : le nom du cabinet, le prénom du destinataire et les questions. Ni
 *   identifiant interne, ni dossier, ni consultation.
 *
 * **Pas d'envoi par SMS.** La spécification le décrivait grisé, « à venir » ; tant qu'aucun
 * fournisseur n'est branché, la voie n'existe pas — ni ici, ni ailleurs. Construire un choix qui
 * n'en est pas un revient à promettre ce qu'on ne tient pas.
 */

/* -------------------------------------------------------------------------- */
/* Questions                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Une question du suivi. `id` est stable et sert de clé dans les réponses : c'est lui qui relie la
 * réponse à sa question quand le modèle du cabinet aura changé de libellé.
 */
export const followUpQuestionSchema = z
  .object({
    id: z.string().trim().min(1).max(60),
    label: z.string().trim().min(1).max(300),
    type: z.enum(followUpQuestionTypes),
  })
  .strict();
export type FollowUpQuestionInput = z.infer<typeof followUpQuestionSchema>;

/** Dix questions au plus : c'est un message de nouvelles, pas un formulaire d'admission. */
export const followUpQuestionsSchema = z.array(followUpQuestionSchema).max(10);

/**
 * Délai d'envoi, en jours. Le **plancher est un paramètre** et non une constante : la recette
 * automatisée a besoin d'un suivi qui parte tout de suite, sans quoi les pages publiques de suivi
 * et d'avis resteraient hors d'atteinte. Le serveur passe le plancher de son environnement
 * (`FOLLOW_UP_MIN_DELAY_DAYS`, qui ne peut descendre sous un jour en production) ; le front, lui,
 * importe les schémas ci-dessous, construits sur la règle du produit.
 */
export const delayDaysSchemaFor = (minDelayDays: number) =>
  z.number().int().min(minDelayDays).max(FOLLOW_UP_MAX_DELAY_DAYS);

/* -------------------------------------------------------------------------- */
/* Modèle du cabinet                                                           */
/* -------------------------------------------------------------------------- */

/** Remplacement complet, comme les réglages de facturation : un champ absent serait ambigu. */
export const followUpTemplateUpdateSchemaFor = (minDelayDays: number) =>
  z
    .object({
      questions: followUpQuestionsSchema,
      defaultDelayDays: delayDaysSchemaFor(minDelayDays),
      enabledByDefault: z.boolean(),
    })
    .strict();

export const followUpTemplateUpdateSchema =
  followUpTemplateUpdateSchemaFor(FOLLOW_UP_MIN_DELAY_DAYS);
export type FollowUpTemplateUpdateInput = z.infer<typeof followUpTemplateUpdateSchema>;

/**
 * Le schéma que la **route déclare** : la forme du corps, bornée à ce que la base accepte. Le
 * serveur la resserre ensuite au plancher de sa configuration. C'est ce qui rend la route typée
 * pour le client RPC tout en laissant la borne venir de l'environnement.
 */
export const followUpTemplateUpdateBodySchema = followUpTemplateUpdateSchemaFor(
  FOLLOW_UP_FLOOR_DELAY_DAYS,
);

export interface FollowUpTemplateDto {
  questions: FollowUpQuestionInput[];
  defaultDelayDays: number;
  enabledByDefault: boolean;
  updatedAt: string;
}

/* -------------------------------------------------------------------------- */
/* Suivi d'une consultation                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Programmation d'un suivi sur une consultation. Les questions sont envoyées en entier et non
 * référencées au modèle : le praticien peut les amender pour ce patient-là, et le suivi garde
 * ensuite sa propre copie.
 */
export const followUpScheduleSchemaFor = (minDelayDays: number) =>
  z
    .object({
      delayDays: delayDaysSchemaFor(minDelayDays),
      questions: followUpQuestionsSchema.min(1),
    })
    .strict();

export const followUpScheduleSchema = followUpScheduleSchemaFor(FOLLOW_UP_MIN_DELAY_DAYS);
/** Idem pour la programmation : forme déclarée ici, borne resserrée par le serveur. */
export const followUpScheduleBodySchema = followUpScheduleSchemaFor(FOLLOW_UP_FLOOR_DELAY_DAYS);
export type FollowUpScheduleInput = z.infer<typeof followUpScheduleSchema>;

export const followUpIdParamSchema = z.object({ id: z.uuid() }).strict();

/** Valeur d'une réponse. La cohérence avec le type de la question est vérifiée par le serveur. */
export const followUpAnswerValueSchema = z.union([
  z.string().trim().max(2000),
  z.number().int().min(0).max(10),
  z.boolean(),
]);

export const followUpAnswersSchema = z.record(
  z.string().trim().min(1).max(60),
  followUpAnswerValueSchema,
);
export type FollowUpAnswersInput = z.infer<typeof followUpAnswersSchema>;

/** Vu depuis le cabinet : l'état du suivi, ses questions et, s'il y en a, les réponses. */
export interface FollowUpDto {
  id: string;
  encounterId: string;
  status: FollowUpStatus;
  delayDays: number;
  scheduledFor: string;
  sentAt: string | null;
  answeredAt: string | null;
  language: Language;
  questions: FollowUpQuestionInput[];
  answers: FollowUpAnswersInput | null;
  createdAt: string;
}

/* -------------------------------------------------------------------------- */
/* Pages publiques                                                             */
/* -------------------------------------------------------------------------- */

/** `<organizationId>.<secret>` : le préfixe ouvre le contexte tenant, le secret seul autorise. */
export const followUpTokenParamSchema = z
  .object({ token: z.string().trim().min(16).max(200) })
  .strict();

export const followUpSubmitSchema = z.object({ answers: followUpAnswersSchema }).strict();
export type FollowUpSubmitInput = z.infer<typeof followUpSubmitSchema>;

/**
 * Ce que la page publique de réponse apprend, et rien de plus : le cabinet qui demande, le prénom
 * de la personne à qui l'on s'adresse, la langue d'affichage et les questions. Aucun identifiant
 * interne — ni dossier, ni consultation, ni suivi — n'en sort : le jeton suffit à tout désigner.
 */
export interface PublicFollowUpDto {
  organizationName: string;
  patientFirstName: string;
  language: Language;
  questions: FollowUpQuestionInput[];
}

/**
 * Accusé de réponse. `reviewToken` n'est présent que si le cabinet collecte des avis : c'est lui
 * qui fait apparaître la carte « Donnez votre avis » après l'envoi.
 */
export interface PublicFollowUpResultDto {
  reviewToken: string | null;
}

export const reviewTokenParamSchema = followUpTokenParamSchema;

export const reviewSubmitSchema = z
  .object({
    rating: z.number().int().min(REVIEW_MIN_RATING).max(REVIEW_MAX_RATING),
    /** Retour privé, facultatif. Il ne quitte jamais le cabinet. */
    feedback: z.string().trim().max(2000).optional(),
  })
  .strict();
export type ReviewSubmitInput = z.infer<typeof reviewSubmitSchema>;

/** Ce que la page d'avis apprend avant de noter : le cabinet, et où mène une note maximale. */
export interface PublicReviewDto {
  organizationName: string;
  language: Language;
  /** Page d'avis publique du cabinet, nulle s'il n'en a pas renseigné. */
  reviewUrl: string | null;
  /** À partir de cette note, le patient est invité à publier son avis. En deçà, retour privé. */
  publicThreshold: number;
}

/** Après la note : l'adresse à ouvrir pour un avis public, ou `null` si le retour reste privé. */
export interface ReviewResultDto {
  redirectUrl: string | null;
}
